<template>
  <div
    class="flex md:max-w-[550px] md:flex-wrap py-4 px-6 gap-2 overflow-x-scroll"
  >
    <LinkedToSelectModal
      variant="create"
      :title="$t('components.task_create.modals.location_title')"
      :description="$t('components.task_create.modals.location_description')"
      :teleport="false"
      :initialState="linkedTo"
      :buildingId="building?.id"
      :multiple="false"
      :defaultTab="['asset', 'asset_group'].includes(space?.type) ? 1 : 0"
      @update="$emit('update:space', $event)"
    >
      <Badge
        :label="space ? space.name : $t('components.filters.linked_to.label')"
        variant="badge"
        :type="space ? 'light' : 'modern'"
        :color="space ? 'blue-light' : 'gray'"
        :border="space ? 'default' : 'dashed'"
        size="lg"
        icon="leading"
        iconName="link-default"
        :disabled="!building"
        :title="
          !building ? $t('components.task_create.please_select_building') : null
        "
      />
    </LinkedToSelectModal>

    <PrioritySelectModal
      variant="create"
      :title="$t('components.task_create.modals.priority_title')"
      :description="$t('components.task_create.modals.priority_description')"
      :initialState="priority"
      :teleport="false"
      @update="$emit('update:priority', $event)"
    >
      <Priority :priority="priority" variant="light" size="lg" />
    </PrioritySelectModal>

    <UserSelectModal
      variant="create"
      :title="$t('components.task_create.modals.assignees_title')"
      :description="$t('components.task_create.modals.assignees_description')"
      :initialState="assignees"
      :buildingIds="buildingIds"
      :teleport="false"
      @update="$emit('update:assignees', $event)"
    >
      <Badge
        :type="assignees?.length ? 'light' : 'modern'"
        :color="assignees?.length ? 'blue-light' : 'gray'"
        :border="assignees?.length ? 'default' : 'dashed'"
        :label="$t('components.assignees.assignees')"
        :disabled="!building"
        :title="
          !building ? $t('components.task_create.please_select_building') : null
        "
        iconName="users-default"
        icon="leading"
        size="lg"
        class="[&_span]:!overflow-visible"
      >
        <template v-if="assignees?.length" #content>
          <div class="flex gap-1 items-center">
            <Icon name="users-default" size="xs" />
            <AvatarGroup :users="assignees" :maxAvatars="2" size="xs" />
          </div>
        </template>
      </Badge>
    </UserSelectModal>

    <DateSelectModal
      variant="create"
      :title="$t('components.task_create.modals.date_select_title')"
      :description="$t('components.task_create.modals.date_select_description')"
      :initialState="date"
      :minDate="new Date()"
      :teleport="false"
      @update="$emit('update:date', $event)"
    >
      <Badge
        :type="date ? 'light' : 'modern'"
        :color="date ? 'blue-light' : 'gray'"
        :border="date ? 'default' : 'dashed'"
        size="lg"
        as="div"
      >
        <template #content>
          <div class="flex gap-1 items-center font-medium">
            <Icon name="calendar-default" size="xs" />
            <DueDate
              :dueDate="date"
              size="md"
              class="font-medium text-quaternary"
              :class="{ 'text-utility-blue-light-700': date }"
            />
          </div>
        </template>
      </Badge>
    </DateSelectModal>

    <CategorySelectModal
      variant="create"
      :title="$t('components.task_create.modals.category_title')"
      :description="$t('components.task_create.modals.category_description')"
      :initialState="category"
      :buildingIds="buildingIds"
      :teleport="false"
      @update="$emit('update:category', $event)"
    >
      <Badge
        :type="category?.name ? 'light' : 'modern'"
        :color="category?.name ? 'blue-light' : 'gray'"
        :border="category?.name ? 'default' : 'dashed'"
        :label="$t('components.filters.category.label')"
        :disabled="!building"
        :title="
          !building ? $t('components.task_create.please_select_building') : null
        "
        size="lg"
      >
        <template #content>
          <div class="flex gap-1 items-center font-medium">
            <Icon name="check-badge" size="xs" />
            <span class="truncate max-w-[220px] inline-block">
              {{ category?.name || $t("components.filters.category.label") }}
            </span>
          </div>
        </template>
      </Badge>
    </CategorySelectModal>

    <TagsSelectModal
      variant="create"
      :title="$t('components.task_create.modals.tags_title')"
      :description="$t('components.task_create.modals.tags_description')"
      :initialState="tags"
      :buildingIds="buildingIds"
      :teleport="false"
      @update="$emit('update:tags', $event)"
    >
      <Badge
        :type="tags?.length ? 'light' : 'modern'"
        :color="tags?.length ? 'blue-light' : 'gray'"
        :border="tags?.length ? 'default' : 'dashed'"
        :label="$t('components.filters.tags.label')"
        :title="
          !building ? $t('components.task_create.please_select_building') : null
        "
        :disabled="!building"
        size="lg"
      >
        <template #content>
          <div class="flex gap-1 items-center font-medium">
            <Icon name="tag" size="xs" />
            {{ selectedTags || $t("components.filters.tags.label") }}
          </div>
        </template>
      </Badge>
    </TagsSelectModal>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { Badge, Priority } from "@/SharedComponents/Badges";
import { DueDate } from "@/SharedComponents/Dates";
import { Icon } from "@/SharedComponents/Icon";
import {
  LinkedToSelectModal,
  DateSelectModal,
  TagsSelectModal,
  UserSelectModal,
  PrioritySelectModal,
  CategorySelectModal,
} from "@/SharedComponents/Modals";
import { AvatarGroup } from "@/SharedComponents/Avatars";

defineEmits([
  "update:space",
  "update:priority",
  "update:assignees",
  "update:date",
  "update:category",
  "update:tags",
]);

const props = defineProps({
  building: { type: Object, required: false },
  space: { type: Object, required: false },
  priority: { type: String, required: false },
  assignees: { type: Array, required: false },
  date: { type: [String, Date, Number], required: false },
  category: { type: Object, required: false },
  tags: { type: Array, required: false },
});

const buildingIds = computed(() =>
  props.building?.id ? [props.building.id] : []
);

const linkedTo = computed(() =>
  props.space ? { buildings: [], children: [props.space.id] } : undefined
);

const selectedTags = computed(() => {
  let _tags = props.tags;
  if (!_tags.length) return null;
  return _tags.length > 2
    ? `${_tags
        .slice(0, 2)
        .map((tag) => tag.name)
        .join(", ")}, +${_tags.length - 2}`
    : _tags.map((tag) => tag.name).join(", ");
});
</script>
